import { Box } from '@mui/material';
import { CircleCheck, Clock, Hourglass, Inbox } from 'lucide-react';
import type { ReactElement } from 'react';

import type { AdminPrivacyRequestSummary } from '@/admin/adminApi';
import { Metric } from '@/ui/kit';

import { daysLabel, meanDelayLabel, nearestCaption, openCaption, ratioLabel } from './privacyRequestsModel';

/**
 * Les quatre tuiles de P12. Chaque nombre est celui de `GET /summary` :
 *
 * - **Ouvertes** : l'effectif, et ce qui le pese (retards, effacements) ;
 * - **Echeance la plus proche** : jours civils rendus par le serveur ;
 * - **Traitees dans les delais · 90 j** : « 14 sur 14 », **jamais un pourcentage
 *   seul** — « 100 % » ne dit pas si c'est 1 sur 1 ou 140 sur 140 — et « — » sans
 *   aucune cloture, car une fraction de zero demande n'est pas « tout va bien » ;
 * - **Delai moyen** : « — » sans cloture, jamais « 0 j ».
 */
export const SummaryTiles = ({ summary }: { readonly summary: AdminPrivacyRequestSummary }): ReactElement => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
    <Metric label="Ouvertes" icon={Inbox} value={summary.open} caption={openCaption(summary)} />
    <Metric
      label="Échéance la plus proche"
      icon={Clock}
      value={summary.nearestDueAt === null ? null : daysLabel(summary.nearestDueDaysRemaining)}
      caption={nearestCaption(summary)}
    />
    <Metric
      label="Traitées dans les délais · 90 j"
      icon={CircleCheck}
      value={ratioLabel(summary.closed90dOnTime, summary.closed90d)}
      caption={summary.closed90d === 0 ? 'Aucune demande clôturée sur 90 jours' : 'demandes clôturées au plus tard à l’échéance'}
    />
    <Metric
      label="Délai moyen · 90 j"
      icon={Hourglass}
      value={meanDelayLabel(summary.meanDelayDays90d)}
      caption={summary.meanDelayDays90d === null ? 'Aucune clôture sur 90 jours' : 'de la réception à la clôture'}
    />
  </Box>
);
